import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import eventBus, { EVENT_NAMES } from '../../../utils/event_bus';
import truckIcon from '../../../static/images/icon_map_truck.png';

// 导入样式和逻辑
import {
  mapContainerStyle,
  mapStyle,
  locationInfoStyle,
  locationInfoContentStyle,
  locationTextStyle,
  closeButtonStyle,
  searchContainerStyle,
  searchInputStyle,
  searchButtonStyle,
  searchResultsStyle,
  searchResultItemStyle,
  toggleButtonStyle
} from './map_view.styles';
import {
  TIANDITU_KEY,
  LocationInfo,
  SearchResult,
  MapMarkerInfo,
  initializeMap,
  locateCurrentPosition,
  searchAddress,
  showSearchResultOnMap,
  updateMarkers
} from './map_view.logic';

interface MapViewProps {
  center?: [number, number]; // 地图中心点坐标 [纬度, 经度]
  zoom?: number; // 缩放级别
  height?: string | number; // 地图容器高度
  width?: string | number; // 地图容器宽度
  onMapReady?: (map: L.Map) => void; // 地图初始化完成回调
  /**
   * @deprecated 使用事件总线代替直接回调，请订阅 EVENT_NAMES.MAP_LOCATION_CHANGE 事件
   * 示例: eventBus.subscribe(EVENT_NAMES.MAP_LOCATION_CHANGE, (lat, lng, address) => { ... })
   */
  onLocationChange?: (latitude: number, longitude: number, address: string) => void; // 位置变更回调（已弃用）
}

// 地图组件对外暴露的方法
export interface MapViewRef {
  updateMapMarkers: (markers: MapMarkerInfo[]) => void; // 更新地图标记点
}

/**
 * 基于Leaflet和天地图的地图组件
 * 可以被其他页面引用
 * 
 * 使用示例：
 * ```tsx
 * // 订阅地图位置变化事件
 * useEffect(() => {
 *   const unsubscribe = eventBus.subscribe(EVENT_NAMES.MAP_LOCATION_CHANGE, 
 *     (latitude, longitude, address) => {
 *       console.log('地图位置变化:', latitude, longitude, address);
 *       // 处理位置变化...
 *     }
 *   );
 *   
 *   // 组件卸载时取消订阅
 *   return () => unsubscribe();
 * }, []);
 * ```
 */
const MapView = forwardRef<MapViewRef, MapViewProps>(({
  center = [35.0, 105.0], // 默认中国中心点
  zoom = 5,
  height = '500px',
  width = '100%',
  onMapReady,
  onLocationChange
}, ref) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const locationMarkerRef = useRef<L.CircleMarker | null>(null);
  const locationCircleRef = useRef<L.Circle | null>(null);
  const distanceCircleRef = useRef<L.Circle | null>(null);
  const markersRef = useRef<Map<string, L.Marker>>(new Map());
  const [currentLocation, setCurrentLocation] = useState<LocationInfo | null>(null);
  const [showLocationInfo, setShowLocationInfo] = useState<boolean>(false);
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [showSearchResults, setShowSearchResults] = useState<boolean>(false);
  const [isSearchResultsCollapsed, setIsSearchResultsCollapsed] = useState<boolean>(false);
  const [showDistanceCircle, setShowDistanceCircle] = useState<boolean>(true);

  // 获取当前位置并在地图上标记
  const handleLocateMe = () => {
    locateCurrentPosition(
      mapInstanceRef,
      locationMarkerRef,
      locationCircleRef,
      setCurrentLocation,
      setShowLocationInfo,
      onLocationChange
    );
  };
  
  // 处理搜索按钮点击
  const handleSearch = () => {
    searchAddress(searchKeyword, setSearchResults, setIsSearching);
    setShowSearchResults(true);
  };
  
  // 处理搜索框输入变化
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchKeyword(e.target.value);
    if (!e.target.value) {
      setSearchResults([]);
      setShowSearchResults(false);
    }
  };
  
  // 处理搜索框按键事件，按回车键搜索
  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };
  
  // 处理搜索结果项点击
  const handleSearchResultClick = (result: SearchResult) => {
    showSearchResultOnMap(
      mapInstanceRef,
      locationMarkerRef,
      locationCircleRef,
      result,
      setCurrentLocation,
      setShowLocationInfo,
      onLocationChange
    );
    // 不再隐藏搜索结果列表
  };

  // 初始化地图
  useEffect(() => {
    return initializeMap(
      mapContainerRef,
      mapInstanceRef,
      center,
      zoom,
      onMapReady,
      handleLocateMe
    );
  }, [center, zoom, onMapReady]);
  
  // 订阅地图点击位置变化事件
  useEffect(() => {
    const unsubscribe = eventBus.subscribe(EVENT_NAMES.MAP_LOCATION_CHANGE, (latitude: number, longitude: number, address: string) => {
      if (!mapInstanceRef.current) return;
      
      const latlng = L.latLng(latitude, longitude);
      
      // 清除之前的标记
      if (locationMarkerRef.current) {
        locationMarkerRef.current.remove();
      }
      if (locationCircleRef.current) {
        locationCircleRef.current.remove();
      }
      
      // 添加位置标记
      locationMarkerRef.current = L.circleMarker(latlng, {
        radius: 8,
        fillColor: '#1976d2',
        color: '#fff',
        weight: 2,
        opacity: 1,
        fillOpacity: 0.8
      }).addTo(mapInstanceRef.current);
      
      // 添加200米半径圆
      locationCircleRef.current = L.circle(latlng, {
        radius: 200,
        fillColor: '#1976d2',
        fillOpacity: 0.1,
        color: '#1976d2',
        weight: 1
      }).addTo(mapInstanceRef.current);
      
      // 更新当前位置状态
      setCurrentLocation({ latlng, address });
      setShowLocationInfo(true);
      
      // 兼容旧的回调方式
      if (onLocationChange) {
        onLocationChange(latitude, longitude, address);
      }
    });
    
    return () => unsubscribe();
  }, [onLocationChange]);
  
  // 保存当前距离值
  const distanceRef = useRef<number | null>(null);
  
  // 订阅距离变化事件，绘制距离圆
  useEffect(() => {
    const unsubscribe = eventBus.subscribe(EVENT_NAMES.DISTANCE_CHANGE, (distance: number) => {
      // 保存距离值以便复选框状态变化时使用
      distanceRef.current = distance;
      
      // 移除旧的距离圆
      if (distanceCircleRef.current) {
        distanceCircleRef.current.remove();
        distanceCircleRef.current = null;
      }
      
      // 只有当showDistanceCircle为true且有当前位置时才绘制圆形
      if (showDistanceCircle && currentLocation && mapInstanceRef.current) {
        // 创建新的距离圆 - 红色虚线
        distanceCircleRef.current = L.circle(
          currentLocation.latlng, // 使用latlng属性
          {
            radius: distance * 1000, // 转换为米
            color: 'red',
            fillColor: 'rgba(255, 0, 0, 0.1)',
            fillOpacity: 0.1,
            weight: 2,
            dashArray: '5, 10', // 虚线样式
          }
        ).addTo(mapInstanceRef.current);
        
        // 调整地图视图以包含整个圆
        mapInstanceRef.current.fitBounds(distanceCircleRef.current.getBounds());
      }
    });
    
    return () => unsubscribe();
  }, [currentLocation, showDistanceCircle]);
  
  // 监听showDistanceCircle状态变化或位置变化，更新距离圆显示
  useEffect(() => {
    // 如果有保存的距离值和当前位置
    if (distanceRef.current && currentLocation && mapInstanceRef.current) {
      // 移除旧的距离圆
      if (distanceCircleRef.current) {
        distanceCircleRef.current.remove();
        distanceCircleRef.current = null;
      }
      
      // 如果需要显示距离圆
      if (showDistanceCircle) {
        // 创建新的距离圆 - 红色虚线
        distanceCircleRef.current = L.circle(
          currentLocation.latlng,
          {
            radius: distanceRef.current * 1000, // 转换为米
            color: 'red',
            fillColor: 'rgba(255, 0, 0, 0.1)',
            fillOpacity: 0.1,
            weight: 2,
            dashArray: '5, 10', // 虚线样式
          }
        ).addTo(mapInstanceRef.current);
      }
    }
  }, [showDistanceCircle, currentLocation]);
  
  // 暴露组件方法给父组件
  useImperativeHandle(ref, () => ({
    // 更新地图标记点
    updateMapMarkers: (markers: MapMarkerInfo[]) => {
      updateMarkers(mapInstanceRef, markersRef, markers);
    }
  }));
  
  // 创建卡车图标
  const createTruckIcon = () => {
    return L.icon({
      iconUrl: truckIcon,
      iconSize: [32, 32],
      iconAnchor: [16, 16],
      popupAnchor: [0, -16]
    });
  };
  
  // 监听司机数据更新事件
  useEffect(() => {
    const unsubscribe = eventBus.subscribe(EVENT_NAMES.DRIVER_DATA_UPDATED, (drivers) => {
      console.log('收到司机数据更新事件:', drivers);
      if (!mapInstanceRef.current || !drivers || !Array.isArray(drivers)) return;
      
      // 将司机数据转换为地图标记点
      const driverMarkers: MapMarkerInfo[] = drivers.map(driver => {
        // 获取司机的经纬度信息
        const lat = (driver as any).lat || (driver.lastPosition?.coordinates?.[1]);
        const lon = (driver as any).lon || (driver.lastPosition?.coordinates?.[0]);
        
        if (!lat || !lon) return null;
        
        // 获取车牌号信息
        let plateInfo = '';
        if (driver.trucks && driver.trucks.length > 0) {
          const truck = driver.trucks[0];
          plateInfo = `车牌: ${truck.plate || '未知'}<br>`;
        }
        
        return {
          latlng: L.latLng(lat, lon),
          title: driver.name || '未知司机',
          content: `${plateInfo}电话: ${driver.phone || '无'}<br>更新时间: ${driver.updatedAt ? new Date(driver.updatedAt).toLocaleString() : '未知'}`,
          type: 'driver',
          id: driver._id
        };
      }).filter(Boolean) as MapMarkerInfo[];
      
      // 更新地图标记点
      if (driverMarkers.length > 0) {
        // 为每个司机标记设置卡车图标
        const map = mapInstanceRef.current;
        const existingMarkers = markersRef.current;
        const updatedMarkerIds = new Set<string>();
        
        // 添加或更新标记点
        driverMarkers.forEach(marker => {
          const markerId = marker.id || `${marker.latlng.lat}-${marker.latlng.lng}-${marker.type}`;
          updatedMarkerIds.add(markerId);
          
          // 如果标记点已存在，则更新其位置和弹出内容
          if (existingMarkers.has(markerId)) {
            const existingMarker = existingMarkers.get(markerId)!;
            existingMarker.setLatLng(marker.latlng);
            existingMarker.setPopupContent(`<div><strong>${marker.title}</strong><p>${marker.content}</p></div>`);
          } else {
            // 创建新的标记点，使用卡车图标
            const newMarker = L.marker(marker.latlng, {
              icon: createTruckIcon()
            }).addTo(map);
            
            // 添加弹出信息
            newMarker.bindPopup(`<div><strong>${marker.title}</strong><p>${marker.content}</p></div>`);
            
            // 将新标记点添加到集合中
            existingMarkers.set(markerId, newMarker);
          }
        });
        
        // 移除不再需要的标记点
        existingMarkers.forEach((marker, id) => {
          if (!updatedMarkerIds.has(id)) {
            marker.remove();
            existingMarkers.delete(id);
          }
        });
        
        // 自动调整地图视图以显示所有标记点
        if (driverMarkers.length > 0) {
          const bounds = L.latLngBounds(driverMarkers.map(m => m.latlng));
          map.fitBounds(bounds, { padding: [50, 50] });
        }
      }
    });
    
    return () => unsubscribe();
  }, []);
  
  // 监听司机选择事件
  useEffect(() => {
    const unsubscribe = eventBus.subscribe(EVENT_NAMES.DRIVER_SELECTED, (driver) => {
      console.log('收到司机选择事件:', driver);
      if (!mapInstanceRef.current || !driver) return;
      
      // 获取司机的经纬度信息
      const lat = (driver as any).lat || (driver.lastPosition?.coordinates?.[1]);
      const lon = (driver as any).lon || (driver.lastPosition?.coordinates?.[0]);
      
      if (!lat || !lon) {
        console.warn('无法获取司机位置信息:', driver);
        return;
      }
      
      const latlng = L.latLng(lat, lon);
      const markerId = driver._id || `${lat}-${lon}-driver`;
      
      // 获取车牌号信息
      let plateInfo = '';
      if (driver.trucks && driver.trucks.length > 0) {
        const truck = driver.trucks[0];
        plateInfo = `车牌: ${truck.plate || '未知'}<br>`;
      }
      
      // 检查标记点是否已存在
      const existingMarkers = markersRef.current;
      
      if (existingMarkers.has(markerId)) {
        // 如果标记点已存在，则聚焦到该标记点并打开弹出窗口
        console.log('标记点已存在:', markerId);
        const marker = existingMarkers.get(markerId)!;
        mapInstanceRef.current.setView(latlng, 15); // 设置地图视图中心点和缩放级别
        marker.openPopup(); // 打开弹出窗口
      } else {
        // 如果标记点不存在，则创建新的标记点
        const newMarker = L.marker(latlng, {
          icon: createTruckIcon()
        }).addTo(mapInstanceRef.current);
        
        // 添加弹出信息
        const content = `<div><strong>${driver.name || '未知司机'}</strong><p>${plateInfo}电话: ${driver.phone || '无'}<br>更新时间: ${driver.updatedAt ? new Date(driver.updatedAt).toLocaleString() : '未知'}</p></div>`;
        newMarker.bindPopup(content).openPopup();
        
        // 将新标记点添加到集合中
        existingMarkers.set(markerId, newMarker);
        
        // 设置地图视图中心点和缩放级别
        mapInstanceRef.current.setView(latlng, 15);
      }
    });
    
    return () => unsubscribe();
  }, []);
  

  return (
    <div style={mapContainerStyle(height, width)}>
      <div 
        ref={mapContainerRef} 
        style={mapStyle} 
      />
      
      {/* 地址搜索框 */}
      <div style={searchContainerStyle}>
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            placeholder="输入地址搜索..."
            value={searchKeyword}
            onChange={handleSearchInputChange}
            onKeyPress={handleSearchKeyPress}
            style={searchInputStyle}
          />
          <button 
            onClick={handleSearch}
            style={searchButtonStyle}
          >
            🔍
          </button>
        </div>
        
        {/* 显示距离圆复选框 */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          padding: '5px 10px', 
          borderTop: '1px solid #eee',
          backgroundColor: '#f9f9f9'
        }}>
          <input
            type="checkbox"
            id="showDistanceCircle"
            checked={showDistanceCircle}
            onChange={(e) => setShowDistanceCircle(e.target.checked)}
            style={{ marginRight: '5px' }}
          />
          <label 
            htmlFor="showDistanceCircle"
            style={{ fontSize: '12px', cursor: 'pointer', color: '#000' }}
          >
            显示搜索范围
          </label>
        </div>
        
        {/* 搜索结果列表 */}
        {showSearchResults && searchResults.length > 0 && !isSearchResultsCollapsed && (
          <div style={searchResultsStyle}>
            {searchResults.map((result, index) => (
              <div 
                key={index}
                onClick={() => handleSearchResultClick(result)}
                style={searchResultItemStyle}
              >
                <div><strong>{result.name}</strong></div>
                <div style={{ fontSize: '12px', color: '#666' }}>{result.address}</div>
              </div>
            ))}
          </div>
        )}
        
        {/* 收起/展开按钮 */}
        {showSearchResults && searchResults.length > 0 && (
          <button 
            onClick={() => setIsSearchResultsCollapsed(!isSearchResultsCollapsed)}
            style={toggleButtonStyle}
          >
            {isSearchResultsCollapsed ? '展开搜索结果 ▼' : '收起搜索结果 ▲'}
          </button>
        )}
        
        {/* 搜索中提示 */}
        {isSearching && (
          <div style={{ padding: '10px', textAlign: 'center', fontSize: '14px', color: '#666' }}>
            搜索中...
          </div>
        )}
        
        {/* 无结果提示 */}
        {showSearchResults && !isSearching && searchResults.length === 0 && searchKeyword && (
          <div style={{ padding: '10px', textAlign: 'center', fontSize: '14px', color: '#666' }}>
            未找到相关地址
          </div>
        )}
      </div>
      
      {/* 位置信息弹窗 */}
      {showLocationInfo && currentLocation && (
        <div style={locationInfoStyle}>
          <div style={locationInfoContentStyle}>
            <div style={locationTextStyle}>
              <strong>当前位置：</strong> {currentLocation.address}
            </div>
            <button 
              onClick={() => setShowLocationInfo(false)}
              style={closeButtonStyle}
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

export default MapView;